import { useEffect, useRef, useState } from "react";
import { Link } from "@inertiajs/react";
import ChatbotComposer from "@/components/chatbot-composer";
import ChatbotMessage from "@/components/chatbot-message";
import ChatbotTypingIndicator from "@/components/chatbot-typing-indicator";
import useChatbotConversation from "@/hooks/use-chatbot-conversation";
import useChatbotReadState from "@/hooks/use-chatbot-read-state";
import { fetchChatbotChat } from "@/components/chatbot-api";
import { chatbot_chat_path } from "@/routes";

// Per-tab on purpose: a floating widget's conversation belongs to the tab it was
// started in, and should not outlive it.
const STORED_CHAT_KEY = "obsero.chatbot.widget.chat_id";

// sessionStorage throws outright in some privacy modes and when site data is
// blocked. The widget lives in the layout on every page, so an exception here
// would take down the whole page rather than just the chat.
function readStoredChatId() {
  try {
    return globalThis.sessionStorage?.getItem(STORED_CHAT_KEY) || null;
  } catch (_error) {
    return null;
  }
}

function writeStoredChatId(chatId) {
  try {
    if (chatId) {
      globalThis.sessionStorage?.setItem(STORED_CHAT_KEY, chatId);
    } else {
      globalThis.sessionStorage?.removeItem(STORED_CHAT_KEY);
    }
  } catch (_error) {
    // Not being able to remember the chat is a degraded experience, not an error.
  }
}

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const { chat, messages, processing, canStop, stopping, stop, error, setError, submit, openChat } = useChatbotConversation();
  const latestMessageRef = useRef(null);
  const rehydratedRef = useRef(false);

  useChatbotReadState({ chatId: chat?.id, messages, active: isOpen });

  useEffect(() => {
    if (chat?.id) writeStoredChatId(chat.id);
  }, [chat?.id]);

  // The widget is unmounted whenever the fullscreen chat page is open (see
  // auth-layout), and on every reload, so it restores itself from the remembered
  // chat id. openChat hands the hook a chat carrying processing: true, and its
  // resume effect picks the answer back up from there.
  useEffect(() => {
    if (rehydratedRef.current || chat || processing) return;

    const storedChatId = readStoredChatId();
    if (!storedChatId) return;

    rehydratedRef.current = true;
    let cancelled = false;

    fetchChatbotChat(storedChatId)
      .then((storedChat) => {
        if (cancelled) return;

        // No chat means it was deleted or is no longer visible - an expected
        // state, not something to show the user.
        if (!storedChat) {
          writeStoredChatId(null);
          return;
        }

        openChat(storedChat, storedChat.messages || []);
      })
      .catch(() => {
        if (!cancelled) writeStoredChatId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [chat, processing, openChat]);

  useEffect(() => {
    if (!latestMessageRef.current) return;

    latestMessageRef.current.scrollIntoView({
      block: "start",
      behavior: "smooth"
    });
  }, [messages, processing, error]);

  const submitQuestion = async (submission) => {
    setIsOpen(true);
    setQuestion("");

    const accepted = await submit(submission);
    if (!accepted) setQuestion(submission.question);

    return accepted;
  };

  return (
    <>
      <button
        type="button"
        className="chatbot-widget__launcher"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        aria-expanded={isOpen}
        aria-controls="chatbot-widget-panel"
        aria-label="Åbn Obsero AI"
      >
        <i className="bi bi-robot" aria-hidden="true" />
      </button>

      {isOpen ? (
        <section id="chatbot-widget-panel" className="chatbot-widget__panel">
          <header className="chatbot-widget__header">
            <div className="chatbot-widget__header-copy">
              <div className="chatbot-widget__eyebrow mb-0">Obsero AI</div>
            </div>

            <div className="chatbot-widget__header-actions">
              {chat ? (
                <Link href={chatbot_chat_path(chat.id)} className="btn btn-light border btn-sm" aria-label="Åbn chat i fuld skærm">
                  <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
                </Link>
              ) : null}

              <button
                type="button"
                className="btn btn-light border btn-sm"
                onClick={() => setIsOpen(false)}
                aria-label="Luk Obsero AI"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="chatbot-widget__messages">
            {messages.length === 0 && !processing ? (
              <div className="chatbot-widget__empty-state">
                <div className="chatbot-widget__empty-icon" aria-hidden="true">
                  <i className="bi bi-robot" />
                </div>
                <div className="chatbot-widget__empty-title">Klar til spørgsmål</div>
                <div className="chatbot-widget__empty-copy">
                  Stil et spørgsmål om dine dokumenter, DORA eller NIS2.
                </div>
              </div>
            ) : null}

            {messages.length > 0 ? (
              messages.map((message, index) => (
                <ChatbotMessage
                  key={message.id}
                  message={message}
                  surface="widget"
                  latestRef={index === messages.length - 1 ? latestMessageRef : null}
                />
              ))
            ) : null}

            {processing ? (
              <ChatbotTypingIndicator surface="widget" latestRef={latestMessageRef} />
            ) : null}
          </div>

          {error ? (
            <div className="alert alert-danger chatbot-widget__error" role="alert">
              {error}
            </div>
          ) : null}

          {chat?.can_message === false ? (
            <div className="alert alert-info chatbot-widget__error" role="status">
              Denne chat er skrivebeskyttet.
            </div>
          ) : (
            <ChatbotComposer
              surface="widget"
              question={question}
              setQuestion={setQuestion}
              processing={processing}
              canStop={canStop}
              stopping={stopping}
              onStop={stop}
              onSubmit={submitQuestion}
              onError={setError}
            />
          )}
        </section>
      ) : null}
    </>
  );
}
